import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { Invoice, PaginationModel } from '$models'
import { invoiceService } from '$services'
import { exceptionMessageHandler, logger } from '$utils'
import { Dayjs } from 'dayjs'
import { isCancelledError } from '@tanstack/react-query'
import { InvoiceStatusFilter } from '$constants'
import {
  extractPaginationModel,
  extractSortModel,
} from '../../../common/helpers'
import { GridSortModel } from '@mui/x-data-grid-pro'

interface InvoicesState {
  invoices: Invoice[]
  paginationModel: PaginationModel
  sortModel: GridSortModel
}

export interface InvoiceQueryConditions {
  start?: Dayjs
  end?: Dayjs
  invoiceNumber?: string
  supplierName?: string
  status?: InvoiceStatusFilter
  exactMatch?: boolean
  page: number
  pageSize: number
  sort?: string
}

const defaultInvoiceQueryConditions = (
  queryCondition: Partial<InvoiceQueryConditions> = {}
) => {
  return {
    start: queryCondition.start,
    end: queryCondition.end,
    invoiceNumber: queryCondition.invoiceNumber,
    supplierName: queryCondition.supplierName,
    status: queryCondition.status,
    exactMatch: queryCondition.exactMatch,
    page: queryCondition.page ?? 0,
    pageSize: queryCondition.pageSize ?? 20,
    sort: queryCondition.sort,
  }
}

export interface InvoicesSlice extends InvoicesState {
  /**
   * Resets the Invoice Slice state
   */
  resetInvoicesSlice: () => void

  /**
   * Get invoices list
   */
  getInvoices(queryConditions?: InvoiceQueryConditions): Promise<void>

  /**
   * HARD delete an invoice file.
   * This should only be used on invoices that have not been worked on e.g. Failed, or Processing invoices
   */
  deleteInvoiceFile: (fileId?: string) => Promise<void>

  /**
   * Sets the invoices state
   * @param invoices The array of invoices to set the invoices state to
   */
  setInvoices: (invoices: Invoice[]) => void

  /**
   * Sets the pagination model for invoice and invoice history Grids
   * @param paginationModel the pagination model to set
   * @returns void
   */
  setPagination: (paginationModel: {
    page: number
    pageSize: number
    total?: number
  }) => void
}

const initialState: InvoicesState = {
  invoices: [],
  paginationModel: {
    page: 0,
    pageSize: 20,
    total: -1, // -1 means the total is unknown
  },
  sortModel: [
    {
      field: 'issuedAt',
      sort: 'desc',
    },
  ],
}

const createInvoicesSlice: StateCreator<
  InvoicesSlice & SharedSlice,
  [],
  [],
  InvoicesSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetInvoicesSlice: () => set(initialState),

  // State API calls
  getInvoices: async (queryConditions): Promise<void> => {
    try {
      const finalQueryConditions =
        defaultInvoiceQueryConditions(queryConditions)
      set(() => ({ isLoading: true }))
      const response = await invoiceService.getInvoices(finalQueryConditions)
      set(() => ({
        invoices: response.result,
        paginationModel: extractPaginationModel(response),
        sortModel: extractSortModel(response),
      }))
    } catch (error) {
      if (isCancelledError(error)) {
        logger.debug('Invoices query was cancelled')
        // We do a return here as we don't want to update the state if the query was cancelled,
        // otherwise it would be an empty array until the last call finishes and return the values
        return
      }

      set(() => ({
        invoices: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve incoming invoices')
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  deleteInvoiceFile: async (fileId?: string): Promise<void> => {
    if (!fileId) return
    return invoiceService.deleteInvoiceFile(fileId).catch((error) => {
      exceptionMessageHandler(error, 'Failed to delete invoice file')
    })
  },

  setInvoices: (invoices: Invoice[]): void => {
    set(() => ({
      invoices: invoices,
    }))
  },

  setPagination: (paginationModel: {
    page: number
    pageSize: number
    total?: number
  }): void => {
    set(() => ({
      paginationModel: {
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        total: paginationModel.total ?? -1,
      },
    }))
  },
})

export default createInvoicesSlice
